/**
 * Purchase wizard (WI-TV-W6-001).
 *
 * Purchase renders through the shared responsive wizard shell — the same header,
 * card, progress indicator, controls, focus handling, inline error association,
 * and submission/thank-you contract the refinance journey already uses — so the
 * two journeys cannot drift visually or behaviorally. This module only supplies
 * the purchase step list, the purchase prefill sanitizer, and the purchase copy.
 */
import type { WizardMachine, WizardStep } from "./wizard-machine";
import type { MaybePromise } from "../callbacks";
import { createPurchaseSteps } from "./purchase-steps";
import { readPurchasePrefillFromMapped } from "./purchase-prefill";
import { createPurchaseSubmitter } from "./purchase-submission";
import type {
  RefinanceSubmissionDeps,
  RefinanceSubmissionOutcome,
  RefinanceSubmitter,
} from "./refinance-submission";
import {
  renderWizardShell,
  type WizardPrefillApplier,
  type WizardSnapshotReader,
  type WizardSwitchProbe,
} from "./wizard-shell";

export interface PurchaseWizardOptions {
  /** Step list override, primarily for tests. Defaults to the locked flow. */
  steps?: WizardStep[];
  /** Initial values, primarily for tests. */
  initialValues?: Record<string, string>;
  /**
   * Submission wiring. Purchase submission is completed by WI-TV-W6-004; when it
   * is absent the wizard is navigation-only.
   */
  submission?: RefinanceSubmissionDeps;
  /** Submitter override, primarily for tests that assert call counts. */
  submitter?: RefinanceSubmitter;
  onComplete?: (values: Readonly<Record<string, string>>) => MaybePromise<void>;
  onSubmitOutcome?: (outcome: RefinanceSubmissionOutcome) => MaybePromise<void>;
  /** Receives the wizard's prefill applier once the wizard is mounted. */
  onPrefillBridge?: (apply: WizardPrefillApplier) => void;
  /** Receives a reader for the wizard's current values once it is mounted. */
  onSnapshotBridge?: (readSnapshot: WizardSnapshotReader) => void;
  /** Wires the shared pre-submit "Change loan type" affordance. */
  changeLoanType?: {
    onActivate: () => void;
    onProbe?: (canSwitch: WizardSwitchProbe) => void;
  };
}

/**
 * Renders the purchase wizard into `root` and returns the {@link WizardMachine}
 * driving it so callers/tests can inspect navigation state.
 */
export function renderPurchaseWizard(
  root: HTMLElement,
  options: PurchaseWizardOptions = {},
): WizardMachine {
  return renderWizardShell(root, {
    steps: options.steps ?? createPurchaseSteps(),
    ...(options.initialValues ? { initialValues: options.initialValues } : {}),
    headerLabel: "Purchase",
    referralNoun: "purchase referral",
    completion: {
      title: "Purchase details complete",
      body:
        "You've reached the end of the purchase steps delivered so far. The remaining " +
        "questions and the submission experience arrive in a later work item.",
    },
    sanitizePrefill: readPurchasePrefillFromMapped,
    submitterFactory: createPurchaseSubmitter,
    ...(options.submission ? { submission: options.submission } : {}),
    ...(options.submitter ? { submitter: options.submitter } : {}),
    ...(options.onComplete ? { onComplete: options.onComplete } : {}),
    ...(options.onSubmitOutcome ? { onSubmitOutcome: options.onSubmitOutcome } : {}),
    ...(options.onPrefillBridge ? { onPrefillBridge: options.onPrefillBridge } : {}),
    ...(options.onSnapshotBridge ? { onSnapshotBridge: options.onSnapshotBridge } : {}),
    ...(options.changeLoanType ? { changeLoanType: options.changeLoanType } : {}),
  });
}
